/**
 * One client's row in My Pipeline — collapsed to a summary, expanded to the
 * full composition of its projected unbooked value.
 *
 * The expanded order is deliberate and is the whole light-touch argument:
 * hygiene first (the quotes carrying nothing, and the ones that cannot be
 * phased into a month at all), then the live quotes that are actually holding
 * the forecast up, then the unseen-work projection. A rep who only ever reads
 * the top of an expanded client still does the most valuable thing available.
 *
 * The "£520k open · £21k in forecast" line above the quotes is the number
 * nobody has ever been shown. It is the difference between the book a rep
 * believes they have and the book the forecast believes they have, and it is
 * what makes the hygiene work feel worth doing rather than like admin.
 */
import {
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CalendarClock,
} from "lucide-react";
import { formatCurrencyGbpCompact } from "@shared-core/domain/formatters";
import { isOverSixMonthsAndWrittenOff } from "@shared-core/domain/my-pipeline-quote-age";
import type {
  PipelineClient,
  PipelineQuote,
} from "@shared-core/data/my-pipeline-types";
import { Link } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { Badge } from "../../../primitives/badge";
import { Button } from "../../../primitives/button";
import { Checkbox } from "../../../primitives/checkbox";
import { QuoteFigures, QuoteLevers, quoteFlagLabel } from "./QuoteLevers";
import type { OnIntervene } from "./QuoteLevers";

export function ClientCompositionCard({
  client,
  expanded,
  monthLabel,
  query,
  hideLongDead,
  selected,
  focusedQuoteId,
  onToggleSelect,
  onToggle,
  onIntervene,
}: {
  client: PipelineClient;
  expanded: boolean;
  monthLabel: string;
  query: string;
  /** Hide quotes past their curve AND over six months old. Presentation only. */
  hideLongDead: boolean;
  selected: Set<string>;
  focusedQuoteId: string | null;
  onToggleSelect: (quoteId: string) => void;
  onToggle: () => void;
  onIntervene: OnIntervene;
}) {
  const q = query.trim().toLowerCase();
  const matching = q
    ? client.quotes.filter(
        (quote) =>
          quote.reference.toLowerCase().includes(q) ||
          quote.description.toLowerCase().includes(q) ||
          client.name.toLowerCase().includes(q),
      )
    : client.quotes;
  // Counted, not just dropped. A filter that silently shortens a rep's book is
  // how they stop trusting the page — so say how many went and let them look.
  const longDead = matching.filter(isOverSixMonthsAndWrittenOff);
  const visible = hideLongDead
    ? matching.filter((quote) => !isOverSixMonthsAndWrittenOff(quote))
    : matching;
  const flagged = visible.filter((quote) => quote.flags.length > 0);
  const healthy = visible.filter((quote) => quote.flags.length === 0);

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center gap-3 rounded-lg px-4 py-3 hover:bg-surface-hover">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={`${expanded ? "Collapse" : "Expand"} ${client.name}`}
          className="shrink-0 text-muted-foreground"
        >
          {expanded ? (
            <ChevronDown className="size-4" />
          ) : (
            <ChevronRight className="size-4" />
          )}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {/* A rep judging whether a quote is really dead wants last-contact
                context, which lives on the customer record. */}
            <Link
              to={paths.customerDetail({ customerId: client.id })}
              className="truncate font-medium hover:text-primary hover:underline"
            >
              {client.name}
            </Link>
            {client.flag_count > 0 && (
              <Badge variant="warning">{client.flag_count} to review</Badge>
            )}
            {client.flag_count === 0 && client.reviewed_at && (
              <Badge variant="success">Reviewed</Badge>
            )}
            {client.unseen.adjustment && (
              <Badge variant="secondary">Unseen adjusted</Badge>
            )}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {formatCurrencyGbpCompact(client.quotes_in_month)} quotes ·{" "}
            {formatCurrencyGbpCompact(client.unseen_in_month)} unseen
            {client.written_off_count > 0 && (
              <> · {client.written_off_count} carrying nothing</>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={onToggle}
          aria-hidden
          tabIndex={-1}
          className="shrink-0 text-right"
        >
          <div className="font-semibold tabular-nums">
            {formatCurrencyGbpCompact(client.projected_unbooked)}
          </div>
          <div className="text-xs text-muted-foreground">projected</div>
        </button>
      </div>

      {expanded && (
        <div className="border-t border-border px-4 py-3 space-y-4">
          <ClientHonestyLine client={client} />

          {flagged.length > 0 && (
            <QuoteGroup
              title="Needs a decision"
              tone="warning"
              quotes={flagged}
              monthLabel={monthLabel}
              selected={selected}
              focusedQuoteId={focusedQuoteId}
              onToggleSelect={onToggleSelect}
              onIntervene={onIntervene}
            />
          )}

          {healthy.length > 0 && (
            <QuoteGroup
              title="Live quotes"
              tone="plain"
              quotes={healthy}
              monthLabel={monthLabel}
              selected={selected}
              focusedQuoteId={focusedQuoteId}
              onToggleSelect={onToggleSelect}
              onIntervene={onIntervene}
            />
          )}

          {hideLongDead && longDead.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {longDead.length} quote{longDead.length === 1 ? "" : "s"} quoted
              over six months ago and carrying nothing{" "}
              {longDead.length === 1 ? "is" : "are"} hidden. Use{" "}
              <span className="font-medium">Show older quotes</span> above to
              see {longDead.length === 1 ? "it" : "them"}.
            </p>
          )}

          <UnseenBlock
            client={client}
            monthLabel={monthLabel}
            onIntervene={onIntervene}
          />
        </div>
      )}
    </div>
  );
}

/**
 * The gap between the open book and what the forecast actually carries.
 * Rendered as a bar so the proportion reads before the numbers do.
 */
function ClientHonestyLine({ client }: { client: PipelineClient }) {
  const carried = client.open_quoted_value
    ? Math.min(
        100,
        (client.quotes.reduce((s, q) => s + q.weighted_remaining_total, 0) /
          client.open_quoted_value) *
          100,
      )
    : 0;

  return (
    <div className="rounded-md bg-surface-subtle px-3 py-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
        <span>
          <strong className="tabular-nums">
            {formatCurrencyGbpCompact(client.open_quoted_value)}
          </strong>{" "}
          <span className="text-muted-foreground">open quotes</span>
        </span>
        <span className="text-muted-foreground">
          carrying{" "}
          <strong className="tabular-nums text-foreground">
            {formatCurrencyGbpCompact(client.quotes_in_month)}
          </strong>{" "}
          in this month
        </span>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-border">
        <div
          className="h-full rounded-full"
          style={{ width: `${carried}%`, background: "#f472b6" }}
        />
      </div>
      {client.written_off_count > 0 && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          {client.written_off_count} quote
          {client.written_off_count === 1 ? " has" : "s have"} aged past the win
          curve and now carry nothing. Deciding them changes the forecast.
        </p>
      )}
    </div>
  );
}

function QuoteGroup({
  title,
  tone,
  quotes,
  monthLabel,
  selected,
  focusedQuoteId,
  onToggleSelect,
  onIntervene,
}: {
  title: string;
  tone: "warning" | "plain";
  quotes: PipelineQuote[];
  monthLabel: string;
  selected: Set<string>;
  focusedQuoteId: string | null;
  onToggleSelect: (quoteId: string) => void;
  onIntervene: OnIntervene;
}) {
  return (
    <div>
      <h4
        className={`mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${
          tone === "warning"
            ? "text-amber-700 dark:text-amber-400"
            : "text-muted-foreground"
        }`}
      >
        {tone === "warning" && <AlertTriangle className="size-3.5" />}
        {title}
      </h4>
      <ul className="divide-y divide-border">
        {quotes.map((q) => (
          <li
            key={q.id}
            className={`flex flex-wrap items-center gap-x-3 gap-y-2 py-2 ${
              focusedQuoteId === q.id
                ? "-mx-2 rounded-md bg-primary/5 px-2 ring-1 ring-primary/30"
                : ""
            }`}
          >
            {/* Selecting several dead quotes and clearing them with one shared
                reason is the difference between a minute and five. */}
            <Checkbox
              checked={selected.has(q.id)}
              onCheckedChange={() => onToggleSelect(q.id)}
              aria-label={`Select ${q.reference}`}
              className="shrink-0"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {/* The quote reference is the way out of this page and into
                    the estimate, where Park / chase lives — the control for a
                    quote that is not lost but should stop sitting in the
                    figures. Without this link the levers here are the only
                    ones a rep can find, which is why three of them asked for
                    a "park" button that already existed. `_self` is the view
                    module's canonical owning-module URL; the frontend reads
                    it rather than constructing one. */}
                <Link
                  to={q._self}
                  className="font-mono text-xs text-muted-foreground hover:text-primary hover:underline"
                  title={`Open ${q.reference} — status, Park / chase, full detail`}
                >
                  {q.reference}
                </Link>
                <span className="truncate text-sm">{q.description}</span>
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                {q.flags.map((f) => (
                  <Badge
                    key={f}
                    variant={f === "written_off" ? "error" : "warning"}
                  >
                    {f === "promised_date_missed" && (
                      <CalendarClock className="size-3" />
                    )}
                    {quoteFlagLabel(f)}
                  </Badge>
                ))}
              </div>
            </div>
            <QuoteFigures
              quote={q}
              monthLabel={monthLabel}
              onIntervene={onIntervene}
            />
            <QuoteLevers
              quote={q}
              monthLabel={monthLabel}
              onIntervene={onIntervene}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function UnseenBlock({
  client,
  monthLabel,
  onIntervene,
}: {
  client: PipelineClient;
  monthLabel: string;
  onIntervene: OnIntervene;
}) {
  const { unseen } = client;
  return (
    <div className="rounded-md border border-dashed border-border px-3 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium">Projected unseen work</div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            Work we expect from {client.name} that has not been quoted yet.
            {unseen.adjustment ? (
              <>
                {" "}
                You set{" "}
                {formatCurrencyGbpCompact(unseen.adjustment.adjusted_value)} —
                &ldquo;{unseen.adjustment.note}&rdquo;
              </>
            ) : (
              <>
                {" "}
                System baseline{" "}
                {formatCurrencyGbpCompact(unseen.baseline_value)}.
              </>
            )}
          </div>
          {unseen.absorbed_value > 0 && (
            <div className="mt-1 text-xs text-muted-foreground">
              {formatCurrencyGbpCompact(unseen.absorbed_value)} already arrived
              and was absorbed, so the projection has decayed to{" "}
              {formatCurrencyGbpCompact(unseen.effective_value)}.
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="font-semibold tabular-nums">
            {formatCurrencyGbpCompact(unseen.effective_value)}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              onIntervene({
                lever: { kind: "unseen" },
                subjectLabel: client.name,
                subjectDetail: "projected unseen work",
                currentValue: unseen.effective_value,
                baselineValue: unseen.baseline_value,
                monthLabel,
              })
            }
          >
            Adjust
          </Button>
        </div>
      </div>
    </div>
  );
}
